import React, { useState } from 'react';
import {
    Table,
    Card,
    Typography,
    Space,
    Input,
    Select,
    Button,
    DatePicker,
    Tag,
    Popconfirm,
    message
} from 'antd';
import {
    SearchOutlined,
    DeleteOutlined,
    ClearOutlined,
    SyncOutlined,
    CodeOutlined,
    BugOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface SystemLogDto {
    id: string;
    timestamp: string;
    level: string;
    renderedMessage: string;
    exception?: string;
    properties?: string;
}

interface PaginatedLogsDto {
    items: SystemLogDto[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
}

export const LogsList: React.FC = () => {
    const queryClient = useQueryClient();
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [level, setLevel] = useState<string | undefined>(undefined);
    const [search, setSearch] = useState<string>('');
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const fetchLogs = async () => {
        const params = new URLSearchParams({
            pageIndex: pageIndex.toString(),
            pageSize: pageSize.toString(),
            ...(level && { level }),
            ...(search && { search }),
            ...(startDate && { startDate }),
            ...(endDate && { endDate })
        });
        const response = await apiClient.get<{ data: PaginatedLogsDto }>(`/admin/logs?${params}`);
        return response.data.data;
    };

    const { data, isLoading, refetch } = useQuery<PaginatedLogsDto>({
        queryKey: ['adminLogs', pageIndex, pageSize, level, search, startDate, endDate],
        queryFn: fetchLogs
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            await apiClient.delete('/admin/logs', { data: { ids } });
        },
        onSuccess: () => {
            message.success('Logs deleted successfully');
            setSelectedRowKeys([]);
            queryClient.invalidateQueries({ queryKey: ['adminLogs'] });
        },
        onError: () => message.error('Failed to delete logs')
    });

    const clearAllMutation = useMutation({
        mutationFn: async () => {
            await apiClient.delete('/admin/logs/clear');
        },
        onSuccess: () => {
            message.success('All logs cleared successfully');
            setSelectedRowKeys([]);
            setPageIndex(1);
            queryClient.invalidateQueries({ queryKey: ['adminLogs'] });
        },
        onError: () => message.error('Failed to clear logs')
    });

    const handleDeleteSelected = () => {
        if (selectedRowKeys.length > 0) {
            deleteMutation.mutate(selectedRowKeys as string[]);
        }
    };

    const handleClearAll = () => {
        clearAllMutation.mutate();
    };

    const getLevelColor = (lvl: string) => {
        switch (lvl) {
            case 'Fatal':
            case 'Error': return 'red';
            case 'Warning': return 'orange';
            case 'Information': return 'blue';
            case 'Debug': return 'green';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (text: string) => new Date(text).toLocaleString()
        },
        {
            title: 'Level',
            dataIndex: 'level',
            key: 'level',
            width: 100,
            render: (lvl: string) => <Tag color={getLevelColor(lvl)}>{lvl}</Tag>
        },
        {
            title: 'Message',
            dataIndex: 'renderedMessage',
            key: 'renderedMessage',
            render: (msg: string) => <Text ellipsis={{ tooltip: msg }} style={{ maxWidth: 400 }}>{msg}</Text>
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_: any, record: SystemLogDto) => (
                <Popconfirm
                    title="Delete this log?"
                    onConfirm={() => deleteMutation.mutate([record.id])}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    const expandedRowRender = (record: SystemLogDto) => {
        let parsedProps = null;
        if (record.properties) {
            try {
                parsedProps = JSON.parse(record.properties);
            } catch {
                parsedProps = record.properties;
            }
        }

        return (
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                {record.exception && (
                    <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ color: 'red' }}><BugOutlined /> Exception:</Text>
                        <pre style={{ background: '#fff', padding: 12, borderRadius: 4, overflowX: 'auto', fontSize: '12px' }}>
                            {record.exception}
                        </pre>
                    </div>
                )}
                {parsedProps && (
                    <div>
                        <Text strong><CodeOutlined /> Properties:</Text>
                        <pre style={{ background: '#fff', padding: 12, borderRadius: 4, overflowX: 'auto', fontSize: '12px' }}>
                            {JSON.stringify(parsedProps, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Card
            title={<Title level={4}>System Logs</Title>}
            extra={
                <Space>
                    <Button icon={<SyncOutlined />} onClick={() => refetch()}>Refresh</Button>
                    <Popconfirm
                        title="Are you sure you want to clear ALL logs?"
                        onConfirm={handleClearAll}
                        okText="Yes, Clear All"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button danger icon={<ClearOutlined />}>Clear All</Button>
                    </Popconfirm>
                </Space>
            }
        >
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                <Input
                    placeholder="Search messages or exceptions..."
                    allowClear
                    prefix={<SearchOutlined />}
                    onPressEnter={(e) => setSearch(e.currentTarget.value)}
                    onBlur={(e) => setSearch(e.target.value)}
                    style={{ width: 250 }}
                />
                <Select
                    allowClear
                    placeholder="Filter by Level"
                    style={{ width: 150 }}
                    onChange={(val) => setLevel(val)}
                    options={[
                        { value: 'Error', label: 'Error' },
                        { value: 'Warning', label: 'Warning' },
                        { value: 'Information', label: 'Information' },
                        { value: 'Debug', label: 'Debug' },
                        { value: 'Fatal', label: 'Fatal' },
                    ]}
                />
                <RangePicker
                    showTime
                    onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) {
                            setStartDate(dates[0].toISOString());
                            setEndDate(dates[1].toISOString());
                        } else {
                            setStartDate(undefined);
                            setEndDate(undefined);
                        }
                    }}
                />
                {selectedRowKeys.length > 0 && (
                    <Popconfirm title={`Delete ${selectedRowKeys.length} selected logs?`} onConfirm={handleDeleteSelected}>
                        <Button danger icon={<DeleteOutlined />}>
                            Delete Selected ({selectedRowKeys.length})
                        </Button>
                    </Popconfirm>
                )}
            </div>

            <Table
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys)
                }}
                columns={columns}
                dataSource={data?.items}
                rowKey="id"
                loading={isLoading || deleteMutation.isPending || clearAllMutation.isPending}
                expandable={{ expandedRowRender }}
                pagination={{
                    current: pageIndex,
                    pageSize: pageSize,
                    total: data?.totalCount || 0,
                    showSizeChanger: true,
                    onChange: (page, size) => {
                        setPageIndex(page);
                        setPageSize(size);
                    }
                }}
            />
        </Card>
    );
};
